import {
  type EventCategory,
  type VoyageEvent,
  type VoyageReportKind,
  type VoyageState,
  type VoyageSummary,
  voyageSummary,
} from '@felix/domain';
import type { Voyage, VoyageReport } from './types.js';

/**
 * Voyage Details, Voyage Timeline, Voyage Summary ve All Reports kartlarının saf yardımcıları
 * (ADR 0082 Ek; ortağın `sefer-3.jpeg`, `sefer-6.jpeg`). Hesaplar domain'de (`voyageSummary`,
 * `reportEvents`); burada seferin raporlarını seçme, ilerleme ve çizelgenin yerleşimi var.
 */

/**
 * Raporun seferi (ADR 0083 §2): sunucunun verdiği `assignedVoyageId` (kendi seferi, bağı ya da eski
 * kalkış bağı); eski sunucuda kendi seferi ya da seferi başlatan sefersiz kalkış (ADR 0058 §3).
 */
export function assignedVoyageIdOf(
  report: VoyageReport,
  voyages: readonly Pick<Voyage, 'id' | 'departureReportId'>[] = [],
): string | null {
  if (report.assignedVoyageId !== undefined) return report.assignedVoyageId;
  if (report.voyageId) return report.voyageId;
  return voyages.find((v) => v.departureReportId === report.id)?.id ?? null;
}

/** Seferin raporları: kendi raporları ve bağlı sefersiz raporlar (ADR 0058 §3, ADR 0083 §2). */
export function voyageReportsOf(reports: readonly VoyageReport[], voyage: Voyage): VoyageReport[] {
  return reports.filter((r) => assignedVoyageIdOf(r, [voyage]) === voyage.id);
}

/**
 * Seferin özeti: iptal edilen raporlar girmez (ADR 0077). Tamamlanan sefer bitişine (sonraki
 * seferin başlangıcı; `voyagePeriods`) kadar, süren sefer son rapora kadar.
 */
export function voyageSummaryOf(voyage: Voyage, reports: readonly VoyageReport[]): VoyageSummary {
  return voyageSummary(
    reports.filter((r) => r.status !== 'void'),
    { end: voyage.status === 'completed' ? voyage.end : null },
  );
}

/** All Reports'un "Active Voyage" kartı: süren sefer, yoksa planlanan. */
export function activeVoyage(voyages: readonly Voyage[]): Voyage | null {
  return (
    voyages.find((v) => v.status === 'active') ??
    voyages.find((v) => v.status === 'planned') ??
    null
  );
}

export interface VoyageProgress {
  /** Son bacağın numarası; seyir yoksa `null`. */
  legNo: number | null;
  actualNm: number;
  plannedNm: number;
  /** Planlanan − gidilen, en az 0; planlanan mesafe yoksa `null`. */
  remainingNm: number | null;
  /** Gidilen / planlanan, en çok %100. */
  completedPct: number | null;
}

/** Seferin ilerlemesi (sefer-6 "Active Voyage": Leg, remaining, Completed). */
export function voyageProgress(voyage: Voyage, summary: VoyageSummary): VoyageProgress {
  const planned = voyage.plannedDistanceNm;
  const actual = summary.distanceNm;
  const known = planned > 0;
  return {
    legNo: summary.legs.length > 0 ? summary.legs.length : null,
    actualNm: actual,
    plannedNm: planned,
    remainingNm: known ? Math.max(0, Math.round((planned - actual) * 10) / 10) : null,
    completedPct: known ? Math.min(100, Math.round((actual / planned) * 100)) : null,
  };
}

/** "Recent Events": en yeni önce. */
export function recentEvents(events: readonly VoyageEvent[], count: number): VoyageEvent[] {
  return [...events].reverse().slice(0, count);
}

export const TIMELINE_FILTERS = ['all', 'reports', 'port', 'anchorage', 'legs'] as const;
export type TimelineFilter = (typeof TIMELINE_FILTERS)[number];

export interface TimelineMarker {
  key: string;
  at: string;
  pct: number;
  type: 'event' | 'report' | 'eta';
  /** Olay türü, rapor türü ya da `eta`. */
  name: string;
  category: EventCategory | 'report' | 'planned';
  reportId: string | null;
  /** Etiket satırı: yakın etiketler çakışmasın diye ikinci satıra iner. */
  row: 0 | 1;
  /** Etiket iki satıra da sığmıyorsa yalnız nokta çizilir (metin ipucunda). */
  labelled: boolean;
  /** Uçtaki etiket çizelgenin içine doğru hizalanır. */
  edge: 'start' | 'end' | null;
  /** Aynı noktaya düşen ve bu işarette birleşen diğerleri. */
  merged: { name: string; at: string }[];
}

export interface TimelineSpan {
  key: string;
  state: VoyageState | 'planned';
  startPct: number;
  widthPct: number;
}

export interface TimelineLabel {
  key: string;
  kind: 'leg' | 'stay' | 'planned';
  no?: number;
  state?: VoyageState;
  startPct: number;
  widthPct: number;
  hours: number;
  distanceNm: number;
}

export interface TimelineModel {
  start: string;
  end: string;
  markers: TimelineMarker[];
  spans: TimelineSpan[];
  labels: TimelineLabel[];
}

export interface TimelineInput {
  summary: VoyageSummary;
  events: readonly VoyageEvent[];
  /** Seferin raporları ("Reports" süzgeci); yalnız gönderilmişler işaretlenir. */
  reports: readonly { id: string; kind: VoyageReportKind; reportedAt: string; status: string }[];
  /** Süren seferde planlanan varış (sefer-6 "ETA Rotterdam"). */
  eta: { at: string; port: string } | null;
  filter: TimelineFilter;
  /** Şeridin piksel genişliği (varsayılan 1000): birleşme ve etiket aralığı buna göre. */
  widthPx?: number;
}

const STAY_STATES: ReadonlySet<VoyageState> = new Set(['port', 'anchorage', 'drift']);
const round = (value: number) => Math.round(value * 1000) / 1000;

/**
 * Yatay çizelgenin yerleşimi (sefer-6 alt bölümü): eksen seferin ilk olayından son raporuna,
 * süren seferde planlanan varışa kadar. Dönemler renkli şerit, bacaklar ve kalışlar şeridin altında
 * etiket, olaylar üstte işaret. 30 px'ten yakın işaretler birleşir; etiketler çakışmasın diye iki satıra
 * dağılır, ikisine de sığmayan etiket yalnız noktadır. Rapor yoksa `null`.
 */
export function timelineModel(input: TimelineInput): TimelineModel | null {
  const { summary, events, eta, filter } = input;
  const widthPx = Math.max(1, input.widthPx ?? 1000);
  if (!summary.start || !summary.end) return null;
  const firstEvent = events[0]?.at;
  const startMs = Math.min(
    Date.parse(summary.start),
    firstEvent ? Date.parse(firstEvent) : Number.POSITIVE_INFINITY,
  );
  const lastMs = Date.parse(summary.end);
  const etaMs = eta ? Date.parse(eta.at) : Number.NaN;
  const planned = eta && etaMs > lastMs ? eta : null;
  const endMs = Math.max(planned ? etaMs : lastMs, startMs + 3_600_000);
  const pct = (iso: string) => round(((Date.parse(iso) - startMs) / (endMs - startMs)) * 100);

  const spans: TimelineSpan[] = summary.periods.map((period, index) => ({
    key: `p${index}`,
    state: period.state,
    startPct: pct(period.start),
    widthPct: round(pct(period.end) - pct(period.start)),
  }));
  const labels: TimelineLabel[] = [
    ...summary.legs.map((leg) => ({
      key: `leg${leg.no}`,
      kind: 'leg' as const,
      no: leg.no,
      startPct: pct(leg.start),
      widthPct: round(pct(leg.end) - pct(leg.start)),
      hours: leg.hours,
      distanceNm: leg.distanceNm,
    })),
    ...summary.periods
      .filter((period) => STAY_STATES.has(period.state))
      .map((period, index) => ({
        key: `stay${index}`,
        kind: 'stay' as const,
        state: period.state,
        startPct: pct(period.start),
        widthPct: round(pct(period.end) - pct(period.start)),
        hours: period.hours,
        distanceNm: period.distanceNm,
      })),
  ].sort((a, b) => a.startPct - b.startPct);
  if (planned) {
    const from = pct(summary.end);
    spans.push({ key: 'planned', state: 'planned', startPct: from, widthPct: round(100 - from) });
    labels.push({
      key: 'planned',
      kind: 'planned',
      startPct: from,
      widthPct: round(100 - from),
      hours: round((etaMs - lastMs) / 3_600_000),
      distanceNm: 0,
    });
  }

  const raw: Omit<TimelineMarker, 'row' | 'merged' | 'labelled' | 'edge'>[] =
    filter === 'reports'
      ? input.reports
          .filter((r) => r.status === 'submitted')
          .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
          .map((r) => ({
            key: `report:${r.id}`,
            at: r.reportedAt,
            pct: pct(r.reportedAt),
            type: 'report',
            name: r.kind,
            category: 'report',
            reportId: r.id,
          }))
      : filter === 'legs'
        ? []
        : events
            .filter((e) => filter === 'all' || e.category === filter)
            .map((e) => ({
              key: e.key,
              at: e.at,
              pct: pct(e.at),
              type: 'event',
              name: e.type,
              category: e.category,
              reportId: e.reportId,
            }));
  if (planned && filter !== 'port' && filter !== 'anchorage') {
    raw.push({
      key: 'eta',
      at: planned.at,
      pct: 100,
      type: 'eta',
      name: 'eta',
      category: 'planned',
      reportId: null,
    });
  }

  // Noktalar 30 px'ten yakınsa birleşir; etiket en çok 140 px genişliğindedir
  const minGap = (30 / widthPx) * 100;
  const labelWidth = (140 / widthPx) * 100;
  const markers: TimelineMarker[] = [];
  for (const marker of raw) {
    const previous = markers.at(-1);
    if (previous && marker.type !== 'eta' && marker.pct - previous.pct < minGap) {
      previous.merged.push({ name: marker.name, at: marker.at });
      continue;
    }
    const edge = marker.pct > 88 ? 'end' : marker.pct < 6 ? 'start' : null;
    markers.push({ ...marker, row: 0, labelled: true, edge, merged: [] });
  }
  // Etiketler iki satıra yerleşir: önce ETA, sonra soldan sağa; sığmayan etiket yalnız noktadır
  const rows: [number, number][][] = [[], []];
  const spanOf = (m: TimelineMarker): [number, number] =>
    m.edge === 'end'
      ? [m.pct - labelWidth, m.pct]
      : m.edge === 'start'
        ? [m.pct, m.pct + labelWidth]
        : [m.pct - labelWidth / 2, m.pct + labelWidth / 2];
  const free = (row: 0 | 1, [a, b]: [number, number]) =>
    rows[row]?.every(([c, d]) => b <= c || a >= d) ?? true;
  for (const marker of [...markers].sort(
    (a, b) => Number(b.type === 'eta') - Number(a.type === 'eta'),
  )) {
    const span = spanOf(marker);
    const row = free(0, span) ? 0 : free(1, span) ? 1 : null;
    if (row === null) marker.labelled = false;
    else {
      marker.row = row;
      rows[row]?.push(span);
    }
  }
  return {
    start: new Date(startMs).toISOString(),
    end: new Date(endMs).toISOString(),
    markers,
    spans,
    labels,
  };
}
